import type { RootState } from 'src/redux/store';

import { debounce } from 'es-toolkit';
import { useBoolean } from 'minimal-shared/hooks';
import React, { useRef, useState, useEffect, useCallback } from 'react';

import {
  Box,
  List,
  Avatar,
  Divider,
  Tooltip,
  Popover,
  ListItem,
  TextField,
  Typography,
  IconButton,
  ListItemText,
  ListItemAvatar,
  ListItemButton,
  CircularProgress,
} from '@mui/material';

import { useTranslate } from 'src/locales';
import { useAppSelector } from 'src/hook/hooks';
import { truncateText } from 'src/pages/chat/utils/get-message';
import { useGetMediaUrlMutation } from 'src/pages/chat/slice/chatApi';
import { useCallContext } from 'src/pages/chat/hooks/use-call-context';
import { useGetInternalUserListMutation } from 'src/pages/setting/slice/settingApi';

import { Iconify } from 'src/components/iconify';
import { useSettingsContext } from 'src/components/settings';

export const InternalCall = () => {
  const { registered, makeCall, handleCallUserDetails } = useCallContext();

  const callMenu = useBoolean(); // ✅ control dialog open/close
  const [getInternalUserList, { isLoading, error }] = useGetInternalUserListMutation();
  const [getMediaUrl] = useGetMediaUrlMutation();
  const [users, setUsers] = useState<any[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);
  const { t } = useTranslate('translation');
  const { loginApiData } = useAppSelector((state) => (state as RootState).auth);
  const listRef = useRef<HTMLUListElement | null>(null);
  const settings = useSettingsContext();

  const getProfileUrl = async (filePath: string | null) => {
    if (filePath) {
      const { data: uploadFileUrl } = await getMediaUrl({ fileName: filePath });
      return uploadFileUrl;
    }
    return '';
  };

  const fetchUsers = async ({
    pageVal,
    search = '',
    reset = false,
  }: {
    pageVal: number;
    search?: string;
    reset?: boolean;
  }) => {
    try {
      const response: any = await getInternalUserList({
        page: pageVal,
        limit: pageSize,
        filters: [
          { fieldname: 'status', search: 'ACTIVE' },
          { fieldname: 'extNumber', sort: 0 },
        ],
        globalSearch: search || undefined,
      }).unwrap();

      const newUsersRaw = response?.list || [];

      const newUsers = await Promise.all(
        newUsersRaw.map(async (user: any) => ({
          ...user,
          userName: user.firstName,
          ProfileURL: await getProfileUrl(user.ProfileURL),
        }))
      );

      setUsers((prev) => {
        if (reset) return newUsers;

        const existingIds = new Set(prev.map((user) => user.id));
        const filtered = newUsers?.filter((user: any) => !existingIds.has(user.id));

        let combined = [...prev, ...filtered];

        if (loginApiData && 'extNumber' in loginApiData && loginApiData?.extNumber) {
          combined = combined?.filter((user) => user.extNumber !== loginApiData.extNumber);
        }

        return combined;
      });

      setTotalPages(Math.ceil(response?.total / pageSize));
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  useEffect(() => {
    if (!callMenu.value) {
      setUsers([]);
      setContacts([]);
      setPage(1);
      setTotalPages(0);
      setSearchQuery('');
    }
  }, [callMenu.value]);

  useEffect(() => {
    if (callMenu.value) {
      fetchUsers({ pageVal: page, search: searchQuery });
    }
  }, [callMenu.value, page]);

  const debounceSearch = useCallback(
    debounce((value: string) => {
      setPage(1);
      fetchUsers({ pageVal: 1, search: value, reset: true });
    }, 500),
    []
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchQuery(value);
    debounceSearch(value);
  };

  const handleUserClick = (user: any) => {
    makeCall(user.extNumber);
    handleCallUserDetails(user);
    callMenu.onFalse();
  };

  const filteredUsers = users.filter((user) =>
    `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredContacts = contacts.filter((contact) =>
    `${contact.name}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleScroll = () => {
    if (
      listRef.current &&
      listRef.current.scrollHeight - listRef.current.scrollTop === listRef.current.clientHeight
    ) {
      if (page < totalPages) {
        setPage((prev) => prev + 1);
      }
    }
  };

  const primaryText = (assignee: any) => {
    const displayText =
      assignee?.firstName && assignee?.lastName
        ? `${assignee.firstName} ${assignee.lastName}`.trim()
        : assignee?.email || '';

    if (displayText?.length > 20) {
      return (
        <Tooltip title={displayText}>
          <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
            {truncateText(displayText, 20)}
          </Typography>
        </Tooltip>
      );
    }

    return displayText;
  };

  return (
    <>
      <IconButton onClick={callMenu.onTrue} disabled={!registered}>
        <Tooltip title={t('INTERNAL_CALL')}>
          <Iconify icon="fluent-person-call-20-filled" />
        </Tooltip>
      </IconButton>

      <Popover
        anchorEl={document.body}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: settings.state.direction === 'ltr' ? 'right' : 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: settings.state.direction === 'ltr' ? 'right' : 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              width: 350,
              minWidth: 350,
              maxHeight: '55vh',
              height: '100%',
              ml: settings.state.direction === 'ltr' ? 'auto' : undefined,
              mr: settings.state.direction === 'rtl' ? 'auto' : undefined,
              top: '7% !important',
              backgroundColor: 'background.default',
              direction: settings.state.direction,
              ...(settings.state.direction === 'ltr'
                ? { left: '40px', right: 'unset' }
                : { right: '40px', left: 'unset' }),
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
        open={callMenu.value}
        onClose={callMenu.onFalse}
      >
        <Box
          sx={{
            p: 2,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              mb: 2,
              textAlign: 'left',
            }}
          >
            {t('INTERNAL_CALL')}
          </Typography>

          <TextField
            size="small"
            fullWidth
            label={t('SEARCH')}
            variant="outlined"
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{ mb: 2 }}
            inputProps={{ dir: settings.state.direction }}
          />

          {isLoading && page === 1 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, flex: 1 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography variant="body2" color="error" sx={{ p: 2 }}>
              Error fetching user list. Please try again.
            </Typography>
          ) : (
            <List
              dense
              ref={listRef}
              component="ul"
              onScroll={handleScroll}
              sx={{
                flex: 1,
                overflowY: 'auto',
                maxHeight: '400px',
                direction: settings.state.direction,
                ...(filteredUsers?.length === 0 && {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }),
              }}
            >
              {selectedTab === 0 && loginApiData && filteredUsers.length > 0 ? (
                filteredUsers.map((user: any) => (
                  <Box key={user.id}>
                    <ListItemButton
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 1,
                        transition: 'background-color 0.2s',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                        alignItems: 'center',
                        px: 2,
                        flexDirection: settings.state.direction === 'rtl' ? 'row-reverse' : 'row',
                        textAlign: settings.state.direction === 'rtl' ? 'right' : 'left',
                      }}
                      disabled={
                        !user.extNumber || ('id' in loginApiData && loginApiData.id === user?.id)
                      }
                    >
                      <ListItemAvatar
                        sx={{
                          mr: settings.state.direction === 'rtl' ? 2 : 1,
                        }}
                      >
                        <Avatar
                          alt={user?.firstName}
                          src={user?.ProfileURL ?? ''}
                          sx={{ width: 36, height: 36 }}
                        />
                      </ListItemAvatar>
                      <ListItemText
                        primary={primaryText(user)}
                        secondary={`Ext: ${user.extNumber || ''}`}
                        primaryTypographyProps={{ fontWeight: 500 }}
                        secondaryTypographyProps={{ color: 'text.secondary' }}
                        slotProps={{
                          primary: {
                            style: {
                              fontWeight: 500,
                              textAlign: settings.state.direction === 'rtl' ? 'right' : 'left',
                            },
                          },
                          secondary: {
                            style: {
                              textAlign: settings.state.direction === 'rtl' ? 'right' : 'left',
                            },
                          },
                        }}
                      />

                      <Tooltip title={t('CALL')}>
                        <IconButton onClick={() => handleUserClick(user)}>
                          <Iconify icon="solar:phone-bold" />
                        </IconButton>
                      </Tooltip>
                    </ListItemButton>
                    <Divider variant="inset" component="li" />
                  </Box>
                ))
              ) : selectedTab === 1 && filteredContacts.length > 0 ? (
                filteredContacts.map((contact: any) => (
                  <Box key={contact.id}>
                    <ListItem
                      onClick={() => handleUserClick(contact.number)}
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 1,
                        transition: 'background-color 0.2s',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                        alignItems: 'center',
                        px: 2,
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar>{contact.name?.[0]}</Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={contact.name}
                        secondary={`Phone: ${contact.number}`}
                        primaryTypographyProps={{ fontWeight: 500 }}
                        secondaryTypographyProps={{ color: 'text.secondary' }}
                      />
                    </ListItem>
                    <Divider variant="inset" component="li" />
                  </Box>
                ))
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ px: 2, py: 1, textAlign: 'center' }}
                >
                  {t('NO_USER_FOUND')}
                </Typography>
              )}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
};
